import React, { useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "react-toastify";
import {
  useDisableCheckInQr,
  useEnableCheckInQr,
  useSeminarCheckInQr,
} from "../../../api/queries/learningQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/dateTime";

/** Full check-in address on this site (the QR encodes it). */
const fullUrl = (checkInPath) =>
  checkInPath
    ? `${window.location.origin}${checkInPath.startsWith("/") ? "" : "/"}${checkInPath}`
    : "";

/** Big QR to show on a screen or print (portal on <body>; print shows only this). */
const QrSheet = ({ seminar, url, qr, onClose }) =>
  createPortal(
    <div
      className="qr-print-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Check-in QR code"
    >
      <div className="qr-sheet-tools no-print">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.print()}
        >
          <i className="bi bi-printer me-1"></i>Print
        </button>
        <button
          type="button"
          className="btn btn-light border"
          onClick={onClose}
        >
          <i className="bi bi-x-lg me-1"></i>Close
        </button>
      </div>
      <h1 className="qr-sheet-title">{seminar.title}</h1>
      <p className="qr-sheet-sub">Scan to check in</p>
      <QRCodeSVG value={url} size={360} level="M" marginSize={2} />
      {qr.opensAt && (
        <p className="qr-sheet-window">
          Open {formatDateTime(qr.opensAt)} – {formatDateTime(qr.closesAt)}
        </p>
      )}
      <p className="qr-sheet-url">{url}</p>
    </div>,
    document.body,
  );

/**
 * Seminar QR check-in (HR): participants scan it and are marked attended.
 * Opens 30 min before the start and closes at the end (server rules).
 * "Replace code" makes old printed QRs stop working.
 */
const CheckInQrCard = ({ seminar, canModify, cancelled }) => {
  const query = useSeminarCheckInQr(seminar.id);
  const enable = useEnableCheckInQr();
  const disable = useDisableCheckInQr();
  const [showSheet, setShowSheet] = useState(false);
  const qr = query.data;
  const url = fullUrl(qr?.checkInPath);
  const busy = enable.isPending || disable.isPending;

  const turnOn = (replace) => {
    if (
      replace &&
      !window.confirm(
        "Replace the code? QR codes already printed or shared will stop working.",
      )
    )
      return;
    enable.mutate(seminar.id, {
      onSuccess: () =>
        toast.success(replace ? "New QR code created." : "QR check-in is on."),
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not turn on QR check-in.")),
    });
  };
  const turnOff = () => {
    if (!window.confirm("Turn off QR check-in? The current QR stops working."))
      return;
    disable.mutate(seminar.id, {
      onSuccess: () => toast.success("QR check-in is off."),
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not turn off QR check-in.")),
    });
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Check-in link copied.");
    } catch {
      toast.info(url);
    }
  };

  return (
    <div className="learn-card h-100">
      <h2 className="learn-subtitle">
        <i className="bi bi-qr-code me-2"></i>QR check-in
      </h2>
      {query.isLoading && (
        <div className="text-center py-3">
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      )}
      {query.isError && (
        <div className="alert alert-danger fs-080 py-2">
          {getApiErrorMessage(query.error, "Could not load the QR settings.")}
        </div>
      )}

      {qr && !qr.enabled && (
        <>
          <p className="fs-085 text-slate-600">
            Participants scan a QR code with their phone to check in. It opens
            30 minutes before the start and closes when the seminar ends.
          </p>
          {canModify && !cancelled && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => turnOn(false)}
              disabled={busy}
            >
              {enable.isPending && (
                <span
                  className="spinner-border spinner-border-sm me-1"
                  aria-hidden="true"
                ></span>
              )}
              Turn on QR check-in
            </button>
          )}
        </>
      )}

      {qr?.enabled && url && (
        <div className="d-flex flex-wrap gap-3 align-items-start">
          <div className="qr-box">
            <QRCodeSVG value={url} size={150} level="M" marginSize={1} />
          </div>
          <div className="flex-grow-1 min-w-0">
            <div className="fs-080 text-slate-600 mb-1">
              <i className="bi bi-clock me-1"></i>
              Opens {formatDateTime(qr.opensAt)}, closes{" "}
              {formatDateTime(qr.closesAt)}
            </div>
            <div className="fs-070 text-slate-500 text-break mb-2">{url}</div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setShowSheet(true)}
              >
                <i className="bi bi-arrows-fullscreen me-1"></i>Fullscreen /
                Print
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light border"
                onClick={copy}
              >
                <i className="bi bi-link-45deg me-1"></i>Copy link
              </button>
              {canModify && !cancelled && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border"
                    onClick={() => turnOn(true)}
                    disabled={busy}
                  >
                    Replace code
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border text-danger"
                    onClick={turnOff}
                    disabled={busy}
                  >
                    Turn off
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showSheet && qr?.enabled && (
        <QrSheet
          seminar={seminar}
          url={url}
          qr={qr}
          onClose={() => setShowSheet(false)}
        />
      )}
    </div>
  );
};

export default CheckInQrCard;
